import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { 
  ShoppingCart, 
  ArrowLeft, 
  ShieldCheck, 
  Zap, 
  FileText, 
  Smartphone,
  CheckCircle2,
  Layout,
  PlayCircle,
  Image as ImageIcon,
  Clock,
  Star,
  Download,
  AlertCircle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Separator } from '@/components/ui/separator';
import { useAuth } from '@/context/AuthContext';
import { db, addDoc, collection, Timestamp, doc, getDoc, query, where, getDocs, orderBy } from '@/firebase';
import { toast } from 'sonner';
import PaymentModal from './PaymentModal';
import ProductDescriptionRenderer from '@/components/ProductDescriptionRenderer';

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, profile, openAuthModal } = useAuth();
  const [product, setProduct] = React.useState<any>(null);
  const [loading, setLoading] = React.useState(true);
  const [isBuying, setIsBuying] = React.useState(false);
  const [showPaymentModal, setShowPaymentModal] = React.useState(false);
  const [currentOrder, setCurrentOrder] = React.useState<{id: string, productName: string, amount: number} | null>(null);

  const [reviews, setReviews] = React.useState<any[]>([]);

  React.useEffect(() => {
    const fetchProductAndReviews = async () => {
      if (!id) return;
      try {
        const docRef = doc(db, 'products', id);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          setProduct({ id: docSnap.id, ...docSnap.data() });
        } else {
          toast.error("Produk tidak ditemukan");
          navigate('/products');
          return;
        }

        // Fetch reviews
        const q = query(
          collection(db, 'productReviews'), 
          where('productId', '==', id)
        );
        const reviewSnapshot = await getDocs(q);
        const fetchedReviews = reviewSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }))
        // Sort in memory to avoid requiring a composite index
        .sort((a: any, b: any) => {
          const dateA = a.createdAt?.toDate ? a.createdAt.toDate().getTime() : 0;
          const dateB = b.createdAt?.toDate ? b.createdAt.toDate().getTime() : 0;
          return dateB - dateA;
        });
        
        setReviews(fetchedReviews);
      } catch (error) {
        console.error("Error fetching product data:", error);
        toast.error("Gagal memuat detail produk");
      } finally {
        setLoading(false);
      }
    };
    fetchProductAndReviews();
  }, [id, navigate]);

  const isApp = product?.category === 'application';

  const handlePurchase = async () => {
    if (!user) {
      toast.info("Silakan masuk atau daftarkan akun baru Anda terlebih dahulu untuk melanjutkan pembelian.");
      openAuthModal('register', `Daftar akun atau masuk untuk membeli "${product?.name || 'Produk'}". File produk digital dan faktur resmi Anda akan tersimpan di akun Anda.`);
      return;
    }

    setIsBuying(true);
    try {
      const orderData = {
        userId: user.uid,
        userEmail: user.email,
        userName: profile?.displayName || user.displayName || 'User',
        userPhone: profile?.phoneNumber || '',
        productId: product.id,
        productName: product.name,
        amount: product.price,
        status: 'pending',
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      };

      const docRef = await addDoc(collection(db, 'orders'), orderData);
      
      setCurrentOrder({
        id: docRef.id,
        productName: product.name,
        amount: product.price
      });
      
      toast.success("Pesanan berhasil dibuat!");
      setShowPaymentModal(true);
    } catch (error) {
      console.error('Purchase error:', error);
      toast.error("Gagal membuat pesanan. Silakan coba lagi.");
    } finally {
      setIsBuying(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <p className="text-foreground">Memuat produk...</p>
      </div>
    );
  }

  if (!product) return null;

  const origin = typeof window !== 'undefined' ? window.location.origin : 'https://azman204.com';
  const canonicalUrl = `${origin}/product/${product.id}`;
  const metaTitle = `${product.name} | AZMAN204`;
  const metaDesc = product.desc?.slice(0, 160) || `Dapatkan ${product.name} format lengkap siap pakai untuk administrasi kontraktor dan tender LPSE.`;
  const metaImage = product.image || product.previewImage || `${origin}/logo.png`;
  const priceNum = typeof product.price === 'number' ? product.price : 0;

  // Schema.org Product & BreadcrumbList JSON-LD
  const productSchema = {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": product.name,
    "description": metaDesc,
    "image": metaImage.startsWith('http') ? metaImage : `${origin}${metaImage.startsWith('/') ? '' : '/'}${metaImage}`,
    "brand": {
      "@type": "Brand",
      "name": "AZMAN204"
    },
    "offers": {
      "@type": "Offer",
      "url": canonicalUrl,
      "priceCurrency": "IDR",
      "price": priceNum,
      "availability": "https://schema.org/InStock",
      "seller": {
        "@type": "Organization",
        "name": "AZMAN204"
      }
    },
    ...(reviews.length > 0 ? {
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": (reviews.reduce((acc, curr) => acc + (curr.rating || 5), 0) / reviews.length).toFixed(1),
        "reviewCount": reviews.length
      }
    } : {})
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Beranda",
        "item": origin
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Katalog Produk",
        "item": `${origin}/products`
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": product.name,
        "item": canonicalUrl
      }
    ]
  };

  return (
    <div className="min-h-screen bg-background pt-32 pb-20">
      <Helmet>
        <title>{metaTitle}</title>
        <meta name="description" content={metaDesc} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:type" content="product" />
        <meta property="og:title" content={metaTitle} />
        <meta property="og:description" content={metaDesc} />
        <meta property="og:image" content={metaImage} />
        <meta property="og:url" content={canonicalUrl} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={metaTitle} />
        <meta name="twitter:description" content={metaDesc} />
        <meta name="twitter:image" content={metaImage} />
        <script type="application/ld+json">{JSON.stringify(productSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      </Helmet>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Button 
          variant="ghost" 
          onClick={() => navigate(-1)}
          className="mb-8 text-foreground/60 hover:text-foreground"
        >
          <ArrowLeft className="mr-2 h-4 w-4" /> Kembali ke Katalog
        </Button>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          {/* Left Column: Visuals & Previews */}
          <div className="lg:col-span-7 space-y-8">
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-card rounded-3xl overflow-hidden shadow-2xl shadow-foreground/5 border border-border"
            >
              <div className="aspect-video bg-muted flex items-center justify-center relative group">
                {isApp ? (
                  product.previewImage ? (
                    <div className="w-full h-full bg-card">
                      <img 
                        src={product.previewImage} 
                        alt="Screenshot Aplikasi" 
                        className="w-full h-full object-contain"
                        referrerPolicy="no-referrer"
                      />
                     </div>
                  ) : (
                    <div className="flex flex-col items-center text-foreground/20">
                      <Layout className="h-24 w-24 mb-4" />
                      <p className="font-bold">Screenshot Aplikasi</p>
                    </div>
                  )
                ) : (
                  <div className="p-4 sm:p-8 w-full h-full overflow-hidden bg-card">
                    {product.previewImage ? (
                      <div className="w-full h-full rounded-xl overflow-hidden shadow-sm border border-border">
                        {(() => {
                          const url = product.previewImage.toLowerCase();
                          const isPdf = url.endsWith('.pdf');
                          const isGoogleDrive = url.includes('drive.google.com');
                          
                          if (isGoogleDrive) {
                            // Convert /view to /preview for Google Drive links
                            const previewUrl = product.previewImage.replace(/\/view(\?.*)?$/, '/preview');
                            return (
                              <div className="w-full h-full overflow-auto">
                                <iframe 
                                  src={previewUrl} 
                                  className="w-full h-[600px] sm:h-full border-none"
                                  title="Preview Google Drive"
                                  allow="autoplay"
                                />
                              </div>
                            );
                          }

                          if (isPdf) {
                            return (
                              <div className="w-full h-full overflow-auto">
                                <iframe 
                                  src={`${product.previewImage}#toolbar=0`} 
                                  className="w-full h-[600px] sm:h-full border-none"
                                  title="Preview PDF"
                                />
                              </div>
                            );
                          }
                          
                          return (
                            <img 
                              src={product.previewImage} 
                              alt="Preview Dokumen" 
                              className="w-full h-full object-contain"
                              referrerPolicy="no-referrer"
                            />
                          );
                        })()}
                      </div>
                    ) : (
                      <div className="border-2 border-border rounded-xl p-8 h-full bg-muted relative">
                        <div className="absolute top-0 left-0 w-full h-24 bg-gradient-to-b from-card to-transparent z-10"></div>
                        <div className="space-y-4 opacity-40 select-none">
                          <div className="h-4 bg-foreground/10 rounded w-3/4"></div>
                          <div className="h-4 bg-foreground/10 rounded w-full"></div>
                          <div className="h-4 bg-foreground/10 rounded w-5/6"></div>
                          <div className="h-4 bg-foreground/10 rounded w-2/3"></div>
                          <div className="h-4 bg-foreground/10 rounded w-full"></div>
                        </div>
                        <div className="absolute inset-0 flex items-center justify-center z-20">
                          <Badge className="bg-primary text-primary-foreground px-4 py-2 text-lg shadow-xl">
                            PREVIEW DOKUMEN
                          </Badge>
                        </div>
                      </div>
                    )}
                  </div>
                )}
                {isApp && (
                  <div className="absolute inset-0 bg-foreground/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-4 pointer-events-none">
                    <Button variant="secondary" className="bg-gold text-navy font-bold pointer-events-auto">
                      <PlayCircle className="h-4 w-4 mr-2" /> Tonton Demo
                    </Button>
                  </div>
                )}
              </div>
            </motion.div>

            <Tabs defaultValue="description" className="w-full text-foreground">
              <TabsList className="bg-transparent border-b border-border w-full justify-between sm:justify-start rounded-none h-auto p-0 flex gap-2 sm:gap-8">
                <TabsTrigger 
                  value="description" 
                  className="rounded-none border-b-2 border-transparent data-[state=active]:border-gold data-[state=active]:bg-transparent data-[state=active]:shadow-none px-1 sm:px-0 py-3 sm:py-4 text-foreground/60 data-[state=active]:text-foreground font-bold text-xs sm:text-base flex-1 sm:flex-none"
                >
                  <span className="hidden sm:inline">Deskripsi Produk</span>
                  <span className="sm:hidden">Deskripsi</span>
                </TabsTrigger>
                <TabsTrigger 
                  value="features" 
                  className="rounded-none border-b-2 border-transparent data-[state=active]:border-gold data-[state=active]:bg-transparent data-[state=active]:shadow-none px-1 sm:px-0 py-3 sm:py-4 text-foreground/60 data-[state=active]:text-foreground font-bold text-xs sm:text-base flex-1 sm:flex-none"
                >
                  <span className="hidden sm:inline">Fitur Utama</span>
                  <span className="sm:hidden">Fitur</span>
                </TabsTrigger>
                <TabsTrigger 
                  value="benefits" 
                  className="rounded-none border-b-2 border-transparent data-[state=active]:border-gold data-[state=active]:bg-transparent data-[state=active]:shadow-none px-1 sm:px-0 py-3 sm:py-4 text-foreground/60 data-[state=active]:text-foreground font-bold text-xs sm:text-base flex-1 sm:flex-none"
                >
                  Manfaat
                </TabsTrigger>
                <TabsTrigger 
                  value="reviews" 
                  className="rounded-none border-b-2 border-transparent data-[state=active]:border-gold data-[state=active]:bg-transparent data-[state=active]:shadow-none px-1 sm:px-0 py-3 sm:py-4 text-foreground/60 data-[state=active]:text-foreground font-bold text-xs sm:text-base flex-1 sm:flex-none"
                >
                  Ulasan ({reviews.length})
                </TabsTrigger>
              </TabsList>
              
              <TabsContent value="description" className="pt-8">
                <ProductDescriptionRenderer content={product.desc} />
              </TabsContent>

              <TabsContent value="features" className="pt-8">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {product.features?.map((feature: string, index: number) => (
                    <div key={index} className="flex items-center gap-3 p-4 bg-card rounded-2xl border border-border">
                      <div className="h-8 w-8 bg-gold/10 rounded-full flex items-center justify-center shrink-0">
                        <Zap className="h-4 w-4 text-primary" />
                      </div>
                      <span className="text-sm font-bold text-foreground">{feature}</span>
                    </div>
                  ))}
                </div>
              </TabsContent>

              <TabsContent value="benefits" className="pt-8">
                <div className="space-y-6">
                  <div className="flex gap-4">
                    <div className="h-12 w-12 bg-primary rounded-2xl flex items-center justify-center shrink-0">
                      <Clock className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                      <h4 className="font-bold text-foreground mb-1">Hemat Waktu 70%</h4>
                      <p className="text-sm text-foreground/60">Tidak perlu membuat dari nol. Gunakan template yang sudah teruji dan fokus pada pekerjaan inti Anda.</p>
                    </div>
                  </div>
                  <div className="flex gap-4">
                    <div className="h-12 w-12 bg-primary rounded-2xl flex items-center justify-center shrink-0">
                      <ShieldCheck className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                      <h4 className="font-bold text-foreground mb-1">Kepatuhan Hukum & Teknis</h4>
                      <p className="text-sm text-foreground/60">Dokumen disusun berdasarkan regulasi konstruksi terbaru untuk meminimalisir risiko kesalahan administrasi.</p>
                    </div>
                  </div>
                  <div className="flex gap-4">
                    <div className="h-12 w-12 bg-primary rounded-2xl flex items-center justify-center shrink-0">
                      <Star className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                      <h4 className="font-bold text-foreground mb-1">Citra Profesional</h4>
                      <p className="text-sm text-foreground/60">Tingkatkan kepercayaan klien dengan dokumen dan sistem yang rapi, terstruktur, dan profesional.</p>
                    </div>
                  </div>
                </div>
              </TabsContent>
              
              <TabsContent value="reviews" className="pt-8">
                <div className="space-y-6">
                  {reviews.length > 0 ? (
                    reviews.map((review: any) => (
                      <div key={review.id} className="p-4 sm:p-6 bg-card rounded-2xl border border-border">
                        <div className="flex justify-between items-start mb-4">
                          <div className="flex items-center gap-3">
                            <div className="h-10 w-10 bg-primary/20 rounded-full flex items-center justify-center font-bold text-primary">
                              {review.userName ? review.userName.charAt(0).toUpperCase() : 'U'}
                            </div>
                            <div>
                              <p className="font-bold text-foreground">{review.userName || 'Pengguna'}</p>
                              <p className="text-xs text-foreground/60">
                                {review.createdAt?.toDate ? new Date(review.createdAt.toDate()).toLocaleDateString('id-ID', { year: 'numeric', month: 'long', day: 'numeric' }) : ''}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center">
                            {[...Array(5)].map((_, i) => (
                              <Star key={i} className={`h-4 w-4 ${i < (review.rating || 0) ? 'text-gold fill-current' : 'text-foreground/20'}`} />
                            ))}
                          </div>
                        </div>
                        {review.reviewText && (
                          <p className="text-sm text-foreground/80 leading-relaxed italic">
                            "{review.reviewText}"
                          </p>
                        )}
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-10">
                      <Star className="h-12 w-12 text-foreground/20 mx-auto mb-4" />
                      <p className="text-foreground/60 font-medium">Belum ada ulasan untuk produk ini.</p>
                      <p className="text-sm text-foreground/40 mt-1">Jadilah yang pertama untuk memberikan ulasan setelah pembelian!</p>
                    </div>
                  )}
                </div>
              </TabsContent>
            </Tabs>
          </div>

          {/* Right Column: Purchase Card */}
          <div className="lg:col-span-5">
            <Card className="border-none shadow-2xl shadow-foreground/10 rounded-3xl overflow-hidden sticky top-32 bg-card">
              <CardContent className="p-8">
                <div className="flex justify-between items-start mb-6">
                  <div>
                    <h1 className="text-3xl font-bold text-foreground font-heading">{product.name}</h1>
                    <div className="mt-3 flex flex-col gap-2">
                      {product.type && (
                        <div className="bg-muted text-blue-600 border border-blue-100 dark:border-blue-900 font-medium py-1.5 px-4 rounded-full text-xs leading-tight w-fit">
                          {product.type}
                        </div>
                      )}
                      <Badge className={`${product.category === 'document' ? 'bg-blue-500/10 text-blue-500' : 'bg-purple-500/10 text-purple-500'} border-none shadow-none hover:bg-transparent px-0 font-bold h-fit py-0`}>
                        {product.category === 'document' ? <FileText className="h-4 w-4 mr-2" /> : <Smartphone className="h-4 w-4 mr-2" />}
                        {product.category === 'document' ? 'Dokumen Digital' : 'Aplikasi Pendukung'}
                      </Badge>
                    </div>
                  </div>
                  <div className="h-12 w-12 bg-muted rounded-2xl flex items-center justify-center">
                    {isApp ? <Layout className="text-foreground" /> : <FileText className="text-foreground" />}
                  </div>
                </div>

                <div className="bg-muted rounded-2xl p-6 mb-8 text-foreground">
                  <p className="text-sm text-foreground/40 mb-1">Harga Investasi</p>
                  <div className="flex items-baseline gap-2">
                    <span className="text-4xl font-black text-foreground">Rp {product.price.toLocaleString('id-ID')}</span>
                    <span className="text-foreground/40 line-through text-sm">Rp {(product.price * 1.5).toLocaleString('id-ID')}</span>
                  </div>
                  <p className="text-xs text-green-600 font-bold mt-2 flex items-center">
                    <Zap className="h-3 w-3 mr-1 fill-current" /> Hemat 33% untuk waktu terbatas!
                  </p>
                </div>

                <div className="space-y-4 mb-8 text-foreground">
                  <div className="flex items-center gap-3 text-sm text-foreground/70">
                    <CheckCircle2 className="h-5 w-5 text-green-500 shrink-0" />
                    <span>Akses Selamanya (Lifetime Access)</span>
                  </div>
                  <div className="flex items-center gap-3 text-sm text-foreground/70">
                    <CheckCircle2 className="h-5 w-5 text-green-500 shrink-0" />
                    <span>Dukungan Teknis Prioritas</span>
                  </div>
                  <div className="flex items-center gap-3 text-sm text-foreground/70">
                    <CheckCircle2 className="h-5 w-5 text-green-500 shrink-0" />
                    <span>Format File: {isApp ? 'Web/Cloud Access' : 'Excel, Word, PDF'}</span>
                  </div>
                </div>

                <Button 
                  onClick={handlePurchase}
                  disabled={isBuying}
                  className={`w-full ${product.appLink ? 'bg-primary/20 text-primary hover:bg-primary/30' : 'bg-primary hover:bg-primary/90 text-primary-foreground'} font-bold h-16 rounded-2xl text-lg shadow-xl shadow-primary/20 group mb-4`}
                >
                  {isBuying ? "Memproses..." : (
                    <>
                      <ShoppingCart className="h-5 w-5 mr-2 group-hover:scale-110 transition-transform" /> 
                      {product.appLink ? "Beli Lisensi Full" : "Beli Sekarang"}
                    </>
                  )}
                </Button>

                {product.appLink && (
                  <Button 
                    asChild
                    className="w-full bg-gold hover:bg-gold/90 text-navy font-black h-16 rounded-2xl text-lg shadow-xl shadow-gold/20 group"
                  >
                    <a href={product.appLink} target="_blank" rel="noopener noreferrer">
                      <Layout className="h-5 w-5 mr-2 group-hover:rotate-12 transition-transform" /> 
                      Buka Web Aplikasi
                    </a>
                  </Button>
                )}

                <div className="mt-6 flex items-center justify-center gap-6">
                  <div className="flex flex-col items-center">
                    <ShieldCheck className="h-5 w-5 text-foreground/20 mb-1" />
                    <span className="text-[10px] text-foreground/40 uppercase font-bold tracking-wider">Aman</span>
                  </div>
                  <Separator orientation="vertical" className="h-8 bg-muted" />
                  <div className="flex flex-col items-center">
                    <Download className="h-5 w-5 text-foreground/20 mb-1" />
                    <span className="text-[10px] text-foreground/40 uppercase font-bold tracking-wider">Instan</span>
                  </div>
                  <Separator orientation="vertical" className="h-8 bg-muted" />
                  <div className="flex flex-col items-center">
                    <Star className="h-5 w-5 text-foreground/20 mb-1" />
                    <span className="text-[10px] text-foreground/40 uppercase font-bold tracking-wider">Premium</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            <div className="mt-8 p-6 bg-gold/5 rounded-3xl border border-gold/10">
              <h5 className="font-bold text-foreground mb-2 flex items-center">
                <AlertCircle className="h-4 w-4 mr-2 text-primary" /> Butuh Bantuan?
              </h5>
              <p className="text-sm text-foreground/60 mb-4">Konsultasikan kebutuhan administrasi Anda dengan tim ahli kami secara gratis.</p>
              <Button variant="outline" className="w-full border-primary text-primary hover:bg-primary hover:text-primary-foreground font-bold rounded-xl bg-transparent">
                Hubungi via WhatsApp
              </Button>
            </div>
          </div>
        </div>
      </div>
      
      <PaymentModal 
        isOpen={showPaymentModal} 
        onClose={() => {
          setShowPaymentModal(false);
          navigate('/dashboard');
        }} 
        order={currentOrder} 
      />
    </div>
  );
}
